import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useAppSelector } from "../../redux";
import { Commet } from "react-loading-indicators";
import { NAME_MAP } from "./consts";

const margin = { top: 80, right: 35, bottom: 150, left: 70 };

const Linechart = ({ width, height }) => {
  const parseTime = d3.timeParse("%Y-%m-%dT%H:%M"); // create function to parse string to time object
  // import data and make into one array
  const graphData = useAppSelector((state) => state.steamKPI).data;

  const ref = useRef();

  // Specify the chart’s dimensions.
  useEffect(() => {
    if (graphData.length === 0) {
      const retryTimeout = setTimeout(() => {
        // Call the useEffect again after 10 seconds
        clearTimeout(retryTimeout);
      }, 10000); // 10 seconds
    }

    // Clear old data
    d3.select(ref.current).selectAll("*").remove();

    // set the dimensions and margins of the graph

    // Create the SVG container.
    const svg = d3
      .select(ref.current)
      .append("g")
      .attr("style", "font: 10px National2;");

    // Select and remove old axis elements
    svg.selectAll(".axis").remove();

    const x = d3
      .scaleUtc()
      .domain(d3.extent(graphData, (d) => parseTime(d.timestamp)))
      .range([margin.left, width - margin.right]);
    console.log(x.domain());

    const y = d3
      .scaleLinear()
      .domain([0, 3200000])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const xAxis = d3
      .axisBottom(x)
      .ticks(width / 300)
      .tickSizeOuter(0)
      .tickFormat(d3.timeFormat("%Y"));

    // Add the horizontal axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .attr("class", "x-axis")
      .call(xAxis)
      .selectAll("text")
      .style("font-size", "10px")
      .style("font-family", "National2");
    const yAxis = d3.axisLeft(y).ticks(5).tickFormat(d3.format(".2s"));

    // Add the vertical axis.
    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(yAxis)
      .call((g) => g.selectAll(".tick line").style("stroke", "#888888"))
      .call((g) => g.selectAll("text").attr("fill", "grey"))
      .call((g) =>
        g
          .append("text")
          .attr("x", -margin.left + 70)
          .attr("y", margin.top - 30)
          .attr("fill", "black")
          .attr("text-anchor", "start")
          .text("Energy Consumption (kWh)")
          .style("font-family", "National2")
          .style("font-size", "15px")
      )
      .style("font-size", "15px")
      .style("font-family", "National2")
      .selectAll(".tick line, .domain")
      .style("stroke", "black");

    // Compute the points in pixel space as [x, y, z], where z is the name of the series.
    const points = graphData
      .map((d) => [
        x(parseTime(d.timestamp)),
        y(d.value),
        NAME_MAP[d.buildingName] || d.buildingName,
      ])
      .filter((point) => point.every((coord) => coord !== undefined));

    // Group the points by series.
    const groups = d3.rollup(
      points,
      (v) => Object.assign(v, { z: v[0][2] }),
      (d) => d[2]
    );
    console.log(groups.values());

    const colorScale = d3
      .scaleOrdinal()
      .domain(groups.keys())
      .range([
        "#AED773",
        "#B5E175",
        "#5EC3E7",
        "#46A37d",
        "#FFA00F",
        "#6285FF",
      ]);

    const dashArray = ["5,5", "10,5", "15,5,5,5", "20,10", "30,10,5,10"];

    // Draw the lines.
    const line = d3.line();
    const path = svg
      .append("g")
      .attr("fill", "none")
      .attr("stroke-width", 3)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .selectAll("path")
      .data(groups.values())
      .join("path")
      .style("mix-blend-mode", "multiply")
      .attr("stroke", (d) => colorScale(d.z))
      .attr("stroke-dasharray", (d, i) => dashArray[i])
      .attr("d", line);

    // Add circles for each data point
    svg
      .append("g")
      .attr("transform", `translate(-20,${height - margin.bottom + 10})`)
      // .call(xAxis)
      .selectAll(".tick")
      .each(function (d) {
        const tick = d3.select(this);
        const xPos = tick
          .attr("transform")
          .replace("translate(", "")
          .split(",")[0];
        graphData.forEach((data) => {
          if (parseTime(data.timestamp).getTime() === d.getTime()) {
            const lineColor = colorScale(
              NAME_MAP[data.buildingName] || data.buildingName
            );
            svg
              .append("circle")
              .attr("cx", xPos)
              .attr("cy", y(data.value))
              .attr("r", 7)
              .attr("fill", lineColor);
          }
        });
      });

    // Add an invisible layer for the interactive tip.
    const dot = svg.append("g").attr("display", "none");

    dot.append("circle").attr("r", 2.5);

    dot.append("text").attr("text-anchor", "middle").attr("y", -8);

    svg
      .on("pointerenter", pointerentered)
      .on("pointermove", pointermoved)
      .on("pointerleave", pointerleft)
      .on("touchstart", (event) => event.preventDefault());

    // When the pointer moves, find the closest point, update the interactive tip, and highlight
    // the corresponding line.
    function pointermoved(event) {
      const [xm, ym] = d3.pointer(event);
      const i = d3.leastIndex(points, ([x, y]) => Math.hypot(x - xm, y - ym));
      const [x, y, k] = points[i];
      path
        .style("stroke", ({ z }) => (z === k ? null : "#ddd"))
        .filter(({ z }) => z === k)
        .raise();
      dot.attr("transform", `translate(${x},${y})`);
      dot.select("text").text(k);
      svg.property("value", graphData[i]).dispatch("input", { bubbles: true });
    }

    function pointerentered() {
      path.style("mix-blend-mode", null).style("stroke", "#ddd");
      dot.attr("display", null);
    }

    function pointerleft() {
      path.style("mix-blend-mode", "multiply").style("stroke", null);
      dot.attr("display", "none");
      svg.node().value = null;
      svg.dispatch("input", { bubbles: true });
    }

    // Define legend dimensions and margin
    const legendWidth = 100;
    // const legendMargin = { top: 420, right: 750, bottom: 10, left: 20 };

    // Create SVG container for legend
    const legend = d3
      .select(ref.current)
      .append("g")
      .attr("transform", `translate(0, ${height - margin.bottom + 20})`);

    // Create legend color scale
    const legendColor = d3
      .scaleOrdinal()
      .domain(groups.keys())
      .range([
        "#AED773",
        "#B5E175",
        "#5EC3E7",
        "#46A37d",
        "#FFA00F",
        "#6285FF",
      ]);

    const keys = Array.from(groups.keys());
    // Separate the keys into two arrays based on the dashArray condition
    const solidKeys = [];
    const dashedKeys = [];
    keys.forEach((key, index) => {
      if (dashArray[index]) {
        dashedKeys.push(key);
      } else {
        solidKeys.push(key);
      }
    });
    const numItems = keys.length;
    const availableWidth = width - margin.left - margin.right;
    const maxCols = Math.min(numItems, Math.floor(availableWidth / 120));
    const numCols = maxCols > 0 ? maxCols : 1;
    const numRows = Math.ceil(numItems / numCols);
    const legendItemWidth = availableWidth / numCols;
    const legendItemHeight = 30;

    // Calculate offsets for both solid and dashed items
    const numRowsSolid = Math.ceil(solidKeys.length / 5);
    const yOffsetDashed = numRowsSolid * 20;

    // Create legend items for all keys with proper positioning
    const legendItems = legend
      .selectAll(".legend-item")
      .data([...solidKeys, ...dashedKeys])
      .enter()
      .append("g")
      .attr("class", "legend-item")
      .attr("transform", (d, i) => {
        const col = i % numCols;
        const row = Math.floor(i / numCols);
        const x = margin.left + col * legendItemWidth;
        const y = row * legendItemHeight;
        return `translate(${x}, ${y})`;
      });

    // Append circles and text
    legendItems
      .append("circle")
      .attr("cx", 10)
      .attr("cy", legendItemHeight / 2)
      .attr("r", 8)
      .attr("fill", (d) =>
        dashArray[keys.indexOf(d)] ? "none" : colorScale(d)
      )
      .attr("stroke", (d) =>
        dashArray[keys.indexOf(d)] ? colorScale(d) : "none"
      )
      .attr("stroke-width", (d) => (dashArray[keys.indexOf(d)] ? 3 : 0))
      .on("click", function (event, d) {
        const selectedKey = d;
        path.style("stroke", (p) =>
          p.z === selectedKey ? colorScale(selectedKey) : "#ddd"
        ); // Highlight corresponding line
      });

    legendItems
      .append("text")
      .attr("x", 30)
      .attr("y", legendItemHeight / 2)
      .attr("dy", "0.4em")
      .attr("font-size", "12px")
      .style("font-family", "National2")
      .text((d) => d)
      .on("click", function (event, d) {
        const selectedKey = d;
        path.style("stroke", (p) =>
          p.z === selectedKey ? colorScale(selectedKey) : "#ddd"
        );
      });
  }, [graphData, height, width, parseTime]);
  return graphData.length === 0 ? (
    <Commet color="#32cd32" size="medium" text="" textColor="" />
  ) : (
    <svg
      id="linechart"
      ref={ref}
      viewBox={`0 0 ${width} ${height}`}
      style={{
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        overflow: "visible",
        font: "10px National2",
      }}
    />
  );
};
export default Linechart;
